import React, { useState, Fragment, useEffect } from 'react'
import { Button, Form, InputGroup, Modal } from 'react-bootstrap';
import ToastComponent from '../Toast/Toast';
import { useTranslation } from 'react-i18next';
import { IVNOSYS, IVNOSYS_CERTIFICATE_PIN_KEY, IVNOSYS_SHOW_MODAL_KEY_PIN } from '../../services/signatureMethods/constants';
import { generateIvSignCompleteSignUri, generateIvSignInitSignUri, generateIvSignPreSignatureLoginUri, generateIvSignSADUri, postPrivSignature } from '../../services/signatureMethods/customServiceSignature';
import LoadingCircle from '../LoadingCircle';
import { generateSad } from '../../services/sad';
import { useSelector } from 'react-redux';

const IvnosysSignature = ({ showModalPin, onClose, onSuccess, certificateSelected, signerGuid }) => {
    const { t } = useTranslation();
    const [certificatePin, setCertificatePin] = useState('');
    const [loadingPreSignatureLogin, setLoadingPreSignatureLogin] = useState(true);
    const [error, setError] = useState(null);
    const [showMobileAuth, setShowMobileAuth] = useState(false);

    const { paginationMeta } = useSelector(state => state.documents);

    const handleError = (err) => {
        setLoadingPreSignatureLogin(false);
        setCertificatePin('');
        onClose();
        const requestErrorMessage = err.response?.data || err.message;
        const errorMessage = requestErrorMessage.includes("Network") ? t('certificates.network-error') : requestErrorMessage;
        setError(errorMessage);
    }

    const handleMFSRedirect = (redirectURL) => {
      // Save important data from sessionStorage to localStorage
      localStorage.setItem(
        "AccessMethod",
        sessionStorage.getItem("AccessMethod")
      );
      localStorage.setItem(
        "oauthTokenB64",
        sessionStorage.getItem("oauthTokenB64")
      );
      localStorage.setItem(
        "appearanceConf",
        sessionStorage.getItem("appearanceConf")
      );
      localStorage.setItem(
        "showApprovals",
        sessionStorage.getItem("showApprovals")
      );
      localStorage.setItem("user", sessionStorage.getItem("user"));

      // Save data of current process to localStorage
      const currentIvnosysProcess = {
        certificateSelected,
        signerGuid,
        certificatePin,
        paginationMeta,
      };

      localStorage.setItem(
        "currentIvnosysProcess",
        JSON.stringify(currentIvnosysProcess)
      );

      window.location.href = redirectURL;
      return;
    };

    const performCompleteSignature = (transactionId) => {
        setLoadingPreSignatureLogin(true);

        const currentIvnosysProcess = JSON.parse(localStorage.getItem("currentIvnosysProcess"));

        const requestPin = certificatePin ? certificatePin : currentIvnosysProcess ? currentIvnosysProcess.certificatePin : '';

        const body = {
            CertGUID: certificateSelected.GUID,
            PIN: requestPin,
        };

        const token = sessionStorage.getItem('oauthTokenB64');

        postPrivSignature(generateIvSignCompleteSignUri(signerGuid, transactionId), body, token)
        .then(() => {
            localStorage.removeItem("currentIvnosysProcess");
            onSuccess(IVNOSYS, IVNOSYS_SHOW_MODAL_KEY_PIN, IVNOSYS_CERTIFICATE_PIN_KEY, certificatePin);
        })
        .catch(handleError)
    }   

    const requestInitSign = (hasMFA, ivnosysToken = null, transactionId = '') => {
        setLoadingPreSignatureLogin(true);

        const body = {
            CertGUID: certificateSelected.GUID,
            Pin: certificatePin,
            Token: ivnosysToken,
            mfa: hasMFA,
            TransactionId: transactionId,
        };

        // Vidcloud token
        const token = sessionStorage.getItem('oauthTokenB64');

        postPrivSignature(generateIvSignInitSignUri(signerGuid), body, token)
        .then(async (response) => {
            const { RedirectUrl, TransactionId, AuthType, SAD } = response.data;

            if (SAD) {
                // SAD info received, calculate SAD payload
                const { PublicKeyBase64, Token, Hash, CertificateId, HashAlgorithm } = SAD;

                const sadPayload = await generateSad(
                    PublicKeyBase64,
                    Token,
                    Hash,
                    CertificateId,
                    certificatePin,
                    HashAlgorithm
                );

                // Send SAD payload to VIDcloud
                const sadBody = {
                    SAD: sadPayload,
                    CertificateGuid: certificateSelected.GUID,
                    TransactionId
                };

                postPrivSignature(generateIvSignSADUri(signerGuid), sadBody, token)
                .then((sadResponse) => {
                    const { RedirectUrl: sadRedirectUrl, TransactionId: sadTransactionId } = sadResponse.data;
                    if (sadRedirectUrl) {
                        // Redirect to MFS
                        handleMFSRedirect(sadRedirectUrl);
                        return;
                    }  
                    
                    performCompleteSignature(sadTransactionId);
                })
                .catch(handleError);
                return;
            }

            // If there is a redirect URL, redirect to MFS
            if (RedirectUrl) {
                handleMFSRedirect(RedirectUrl);
                return;
            }

            if (AuthType === 'mobile') setShowMobileAuth(true);

            performCompleteSignature(TransactionId);
        })
        .catch(handleError);
    }

    const requestPreSignatureLogin = () => {
        const body = {
            CertGUID: certificateSelected.GUID,
        };

        const token = sessionStorage.getItem('oauthTokenB64');

        postPrivSignature(generateIvSignPreSignatureLoginUri(signerGuid), body, token)
        .then((response) => {
            const { RedirectUrl, Token } = response.data;

            // Save data of current process to localStorage
            const currentIvnosysProcess = {
                certificateSelected,
                signerGuid,
                Token,
                paginationMeta
            };

            localStorage.setItem("currentIvnosysProcess", JSON.stringify(currentIvnosysProcess));

            if (RedirectUrl) {
                // Save important data from sessionStorage to localStorage
                localStorage.setItem('AccessMethod', sessionStorage.getItem('AccessMethod'));
                localStorage.setItem('oauthTokenB64', sessionStorage.getItem('oauthTokenB64'));
                localStorage.setItem('appearanceConf', sessionStorage.getItem('appearanceConf'));
                localStorage.setItem('showApprovals', sessionStorage.getItem('showApprovals'));
                localStorage.setItem('user', sessionStorage.getItem('user'));

                window.location.href = RedirectUrl;
                return;
            }
            
            // If the certificate requires pin, show the modal
            if (certificateSelected.PinRequired) {
                setLoadingPreSignatureLogin(false);
                return;
            }

            requestInitSign(false, Token, '');
        })
        .catch(handleError);
    }

    useEffect(() => {
        // Reset the certificate pin when the modal is closed
        if (!showModalPin) {
            setCertificatePin('');
            return;
        }

        // If the modal is shown, request presignature login
        if (certificateSelected && certificateSelected.GUID) {
            // Check URL params for Ivnosys actions
            const urlParams = new URLSearchParams(window.location.search);
            const ivnosysPreSignAuth = urlParams.get('ivnosysPreSignAuth');

            if (ivnosysPreSignAuth) {
                const transactionId = urlParams.get('transactionid');

                if (!transactionId) {
                    setError("Missing transaction ID");
                    setLoadingPreSignatureLogin(false);
                    return;
                }
                setLoadingPreSignatureLogin(false);
                return;
            }

            const ivnosysInitSign = urlParams.get('ivnosysInitSign');
            if (ivnosysInitSign) {
                const transactionId = urlParams.get('transactionid');
                performCompleteSignature(transactionId);
                return;
            }

            requestPreSignatureLogin();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showModalPin, certificateSelected]);

    const handlePinSubmit = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const transactionId = urlParams.get('transactionid');

        // Get token
        const currentIvnosysProcess = JSON.parse(localStorage.getItem("currentIvnosysProcess"));
        const { Token } = currentIvnosysProcess;
        const ivnosysToken = Token;

        const hasMFA = !!(transactionId && ivnosysToken);

        requestInitSign(hasMFA, ivnosysToken, transactionId);
    }

    return (
        <Fragment>
            <Modal show={showModalPin} onHide={() => {onClose(); setCertificatePin('')}}>
                {loadingPreSignatureLogin && 
                <>
                    <LoadingCircle centerVertically={true} />
                    {showMobileAuth && <p className="text-center">{t('documents.ivnosys-mobile-auth')}</p>}
                </>
                }
                {
                    !loadingPreSignatureLogin &&
                    <>
                        <Modal.Header closeButton>
                            <Modal.Title>{t('documents.sw-pin')}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <InputGroup className="mb-3">
                                <InputGroup.Prepend>
                                    <InputGroup.Text
                                        id="inputGroup-sizing-default">{t('general.pin')}:
                                    </InputGroup.Text>
                                </InputGroup.Prepend>
                                <Form.Control
                                    required
                                    type="password"
                                    aria-label="Default"
                                    aria-describedby="inputGroup-sizing-default"
                                    autoComplete="new-password"
                                    onChange={e => setCertificatePin(e.target.value)}
                                />
                            </InputGroup>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button className="reject" onClick={() => {onClose(); setCertificatePin('')}}>
                                {t('general.cancel')}   
                            </Button>
                            <Button variant="primary" disabled={!certificatePin}
                                onClick={() => handlePinSubmit()}>
                                {t('general.confirm')}
                            </Button>
                        </Modal.Footer>
                    </>
                }
            </Modal>
            <ToastComponent
                onClose={() => setError(null)}
                show={!!error}
                title={t('general.error')}
                message={error}
                variant="error"
                closeButton
                zIndex={11}
            />
        </Fragment>
    );
}

export default IvnosysSignature
